/**
 * Custom CheckBox Component
 *
 * Replaces the removed React Native CheckBox component.
 * Uses TouchableOpacity and Text to create a checkbox interface.
 * NOW THEME-AWARE
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  GestureResponderEvent,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';

interface CheckBoxProps {
  value: boolean;
  onValueChange: (newValue: boolean) => void;
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
  tintColors?: { true: string; false: string };
}

export function CheckBox({
  value,
  onValueChange,
  disabled = false,
  testID,
  style,
  tintColors,
}: CheckBoxProps) {
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const handlePress = (event: GestureResponderEvent) => {
    if (!disabled) {
      onValueChange(!value);
    }
  };

  const textColor = value
    ? tintColors?.true || theme.colors.primary
    : tintColors?.false || theme.colors.textTertiary;

  return (
    <TouchableOpacity
      testID={testID}
      onPress={handlePress}
      disabled={disabled}
      style={[styles.checkbox, disabled && styles.checkboxDisabled, style]}
      activeOpacity={0.7}
    >
      <Text style={[styles.checkboxText, { color: textColor }]}>{value ? '☑' : '☐'}</Text>
    </TouchableOpacity>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    checkbox: {
      width: 24,
      height: 24,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      borderRadius: theme.radii.sm,
      backgroundColor: theme.colors.surface,
    },
    checkboxDisabled: {
      opacity: 0.5,
    },
    checkboxText: {
      fontSize: 18,
    },
  });
}
